/**
 * CommitmentTerms.tsx — the explicit commitment + terms gate before delivery.
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * WHY (2026-08-14, CTO): the patient is choosing a 1/3/6/12-month plan that is
 * BILLED MONTHLY, so the screen reads like a rolling subscription while the
 * contract is a fixed term. Nothing on the page said so. A patient who believed
 * they could stop after month one and then gets charged has a legitimate
 * complaint — and for a healthcare provider that is a regulatory problem, not
 * just a support ticket. This makes the commitment explicit, states it in the
 * patient's own numbers, and requires an affirmative tick before checkout can
 * continue.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️  THE WORDING BELOW IS A DRAFT AND MUST NOT GO LIVE WITHOUT LEGAL SIGN-OFF.
 *
 * The mechanism (explicit consent, recorded, gating the CTA) is sound and is the
 * part worth building now. The TERMS themselves carry UK consumer-law risk that
 * a developer must not settle:
 *
 *   1. CANCELLATION RIGHTS CANNOT BE CONTRACTED AWAY. Under the Consumer
 *      Contracts (Information, Cancellation and Additional Charges) Regulations
 *      2013 a distance contract carries a 14-day cooling-off right. Where the
 *      patient asked for the service to start inside that window they may still
 *      cancel and owe only a pro-rata amount for what was supplied. A term that
 *      says otherwise is unenforceable, and stating it on-screen is itself a
 *      misleading-practice risk.
 *   2. THE EARLY-EXIT FEE HAS BEEN REMOVED (2026-08-28) — do not put it back
 *      without both a legal review and an implementation. It was never
 *      implemented: nothing in orders-service, clinic-service or Stripe ever
 *      charged it, so the screen stated a charge that could not happen. On its
 *      own that is a misleading price statement. It also could not survive the
 *      CTO decision to lock every plan to three months and let the patient stop
 *      future months from their own account: a fee for leaving contradicts the
 *      promise that makes the lock fair. And Consumer Rights Act 2015 Part 2
 *      would have required "one month" to be justified against real cost
 *      (dispensed stock, clinician time) rather than chosen as a round number.
 *   3. SUBSCRIPTION RULES ARE TIGHTENING. The Digital Markets, Competition and
 *      Consumers Act 2024 adds reminder-notice and easy-exit duties for
 *      subscription contracts. Terms written today should anticipate them. The
 *      one-press stop in the patient's own account is built with those duties in
 *      mind; the reminder notice before each renewal is NOT built yet. That gap
 *      matters more since 2026-09-04, when the 1-month term became selectable
 *      again: it does not renew, so the patient carries the whole burden of
 *      remembering. Nothing here may promise them a reminder until one exists —
 *      what the screen does instead is state the non-renewal at the point of
 *      choice (`checkout/PlanSelection`'s `termEndNote`) and in the first
 *      point below.
 *   4. THERE MUST BE A CLINICAL CARVE-OUT, AND IT IS NON-NEGOTIABLE. If a
 *      clinician stops treatment, or the patient suffers side effects, or they
 *      become ineligible, they must NOT face an exit fee. Charging a patient for
 *      stopping a medicine on clinical grounds would be indefensible to the CQC
 *      and the GPhC — and it is the single most likely complaint this product
 *      will generate. The carve-out is stated below and must survive review.
 *
 * ⚠️ AND EVERY SENTENCE HERE MUST DESCRIBE WHAT THE SYSTEM ACTUALLY DOES. The
 * exit fee above is the cautionary tale: plausible copy, no code behind it, on
 * the one screen where a patient commits money. Before editing a bullet, find
 * the code that makes it true.
 *
 * Route the draft through the DPO/legal reviewer, then replace this block with
 * the approved copy and record who approved it and when.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * WHERE THE LINK GOES, AND WHY IT IS NOT OURS (2026-09-04, issue #308). The
 * "terms and conditions" anchor reads `TERMS_URL` from `lib/bookingSite.ts` and
 * points OFF-ORIGIN at the booking site's marketplace terms. It used to be a
 * local `'/terms'`, a route this portal has never had, so the patient who ticked
 * this box and followed the link they had just agreed to landed on "We can't
 * find that clinic" — see that module for the whole account, including why the
 * answer is not a `/terms` redirect route. THREE surfaces render this component
 * and all three are contract moments: checkout (PlanSelection), the emailed
 * end-of-term confirmation (/resubscribe) and the in-portal re-subscribe card.
 * `checkout/termsLink.spec.tsx` pins the href on all three against the route
 * table, so a terms link aimed at a route nobody added fails CI instead of
 * shipping. Whether those terms COVER these subscriptions — all four of the
 * 1/3/6/12-month terms again since 2026-09-04, not the single locked three-month
 * one — is the unanswered question in the block above, and the link resolving
 * does not answer it.
 *
 * Accessibility: a real <input type="checkbox"> with a <label>, not a styled
 * div — it must be reachable and toggleable by keyboard and announce its checked
 * state. The blocked CTA explains WHY it is blocked in text tied to the control
 * via aria-describedby, never colour or a disabled attribute alone.
 *
 * ── WHY THIS IS THREE EXPORTS AND NOT ONE (2026-09-04) ──────────────────────
 * {@link CommitmentTerms} is unchanged for the two surfaces that render it in
 * ordinary page flow (/resubscribe and the in-portal re-subscribe card): one
 * panel, bullets then tick. The plan step cannot use it, because its rail is now
 * a scrolling region with a PINNED FOOTER — the long "what you're agreeing to"
 * list is what scrolls, and the CTA has to stay reachable without hunting for a
 * nested scrollbar (see `.gps-plan-aside`). So the two halves are exported
 * separately for it to place.
 *
 * ⚠️ THE TICK GOES WITH THE BUTTON, WHEREVER THEY ARE PUT. The tick states the
 * commitment in this patient's own numbers and is the CONDITION on the button;
 * pinning the button while the tick scrolled out of sight would leave somebody
 * pressing a control whose condition they cannot see, on a screen where the
 * condition is a months-long contract for a prescription medicine. Split the
 * panel however the layout needs — never split those two.
 *
 * ⚠️ AND THE BULLETS STILL COME FIRST IN THE DOM. That is a consent order, not a
 * visual one: it is what a screen reader reads and what Tab follows.
 */
import { formatPence } from '../lib/format';
import { TERMS_URL } from '../lib/bookingSite';
import { IconCalendar, IconPriceTag, IconRefresh, IconStethoscope } from '../ui/icons';
import { IconPoints, type IconPoint } from '../ui/IconPoints';
import { SummaryEyebrow } from './OrderSummary';

/**
 * The panel's own frame, exported so the plan step's scrolling rail can wrap the
 * bullets in the SAME box the two flow surfaces get. Copying the five
 * declarations into PlanSelection would let the contract panel look like two
 * different components on two screens that are both contract moments. Both
 * boxes also wear `gps-jux-panel`, the journey's white card (tokens.css, P7).
 */
export const commitmentPanelStyle: React.CSSProperties = {
  background: 'var(--gps-surface-2)',
  border: '1px solid var(--gps-border)',
  borderRadius: 'var(--gps-r-lg)',
  padding: 'var(--gps-s-4)',
};

/**
 * The agreement's words run in full body ink. `IconPoints compact` sets its
 * sentences in the quieter help ink (right for q14's acknowledgements, which a
 * patient reads rather than scans); a term of the contract is not fine print.
 */
const AGREEMENT_INK: React.CSSProperties = { color: 'var(--gps-text)' };

/**
 * THE CLINICAL CARVE-OUT, in one place for both term shapes (the header's point
 * 4 — non-negotiable). It is origin/dev's sentence, word for word: the three
 * clinical grounds it named (the clinician stops the treatment, side effects,
 * no longer suitable) and "your plan stops and nothing further is charged",
 * which is what the ADMIN `cancel-now` lane does (see the ⚠️ on
 * `CommitmentTermsDetail`). The dedupe's first wording narrowed it to the
 * clinician's stop alone and leaned on "stopping is always free" for the rest;
 * with "always free" gone (beside a term that keeps billing when the patient
 * stops, it reads as "costs nothing more" — the promise point 2 had to take
 * back), the three grounds are named again rather than dropped (review,
 * 2026-09-26). Narrowing a carve-out is a legal call, not a copy edit.
 */
const CARVE_OUT_LEAD = 'If your clinician stops your treatment';
const CARVE_OUT_REST =
  ', you have side effects, or you become unsuitable to continue, your plan stops and nothing further is charged.';

/**
 * WHAT THEY ARE AGREEING TO — the heading and the points, with no tick.
 *
 * Rendered on its own only by the plan step, where it is the part that SCROLLS.
 * It carries no box of its own for that reason: the rail's scrolling region
 * supplies the frame (see `commitmentPanelStyle`), because a bordered box inside
 * a scrolling box reads as two nested panels.
 *
 * EACH FACT ONCE, SHORT, IN THE PATIENT'S WORDS (2026-09-26, the CTO reviewing
 * stage: "we are saying the same thing over and over in different places;
 * reduce the text"). Four bullets became three icon-led points — the card
 * step's hold box's own shape (`ui/IconPoints`, a decorative glyph and a bold
 * lead that runs straight into its sentence), so the two checkout screens read
 * as one family. The line that repeated the stop promise under the term cards
 * (`termEndNote`, multi-month) and the one that repeated "not charged until a
 * clinician approves" under the button were removed from PlanSelection in the
 * same change; the button keeps ONE no-charge line
 * (`journey/serviceClaims` `NO_CHARGE_UNTIL_APPROVED`).
 *
 * THE CONSUMER-CONTRACT FACTS IN THE HEADER ABOVE, EACH SAID ONCE:
 *   - the term, the monthly price and the total, and that it is BILLED MONTHLY
 *     (point 1);
 *   - the plan RUNS FOR THE WHOLE TERM, and stopping from the account does not
 *     end it early — with the CLINICAL CARVE-OUT beside it, stated with the
 *     charges, not buried in the linked terms (point 2; on a one-month plan,
 *     point 3);
 *   - ONLY MONTH 1 is paid at approval, and nothing if not approved (the
 *     approval point) — the single most common misreading of this panel is that
 *     the total leaves the account at once;
 *   - the one-month plan ENDS and does NOT renew (its first point).
 *
 * ⚠️ POINT 2 USED TO PROMISE THE OPPOSITE OF WHAT orders-service DOES, AND THAT
 * WAS PROVEN, NOT OPEN (review, 2026-09-26). It read "Stop future months any
 * time from your account — you only pay for the months you've had" (and before
 * this change "you are only ever charged for the months you have had, and there
 * is no charge for stopping"). orders-service `dev` (7dba36e) says otherwise in
 * so many words: `SubscriptionsService.cancel()` — the only stop a PATIENT can
 * press — "records cancel-at-term-end intent (no re-enrol) and leaves the plan
 * billing to the end of its commitment — never an immediate stop or refund";
 * `order-states.ts` — a patient who pressed "Stop future months" "is still
 * active and their remaining committed months are STILL CHARGED". The patient
 * dashboard already tells them so ("Your plan is committed for N months, and
 * stopping does not end it early" — `lib/dashboardSummary.ts`
 * `stopFutureMonthsCopy`; CLAUDE.md "STOP FUTURE MONTHS DOES NOT STOP THE
 * COMMITTED TERM, AND THE COPY MUST NOT SAY IT DOES"). So point 2 now says what
 * the code does, in the dashboard's words. If the product should let a patient
 * stop the months ahead, the change is in orders-service FIRST, and this point
 * and the dashboard's sentence flip back together in the same release.
 *
 * THE CLINICAL CARVE-OUT rests on the ADMIN lane: `adminCancelNow()`
 * (`POST /v1/orders/subscriptions/:id/cancel-now`, the admin Payments section's
 * "cancel now") flips the plan to `cancelled` and clears `nextBillingAt`, so no
 * further month bills. It is an operator's action — nothing calls it
 * automatically when a clinician stops treatment or a patient reports side
 * effects — so the promise holds while an operator presses it. It is
 * origin/dev's sentence unchanged, so it says no more than the screen already
 * did; that it depends on a manual step is for the CTO / legal, with the stop
 * point above.
 *
 * This component also renders on /resubscribe and the in-portal re-subscribe
 * card. There, confirming places a hold and a clinician must approve the NEW
 * order before anything is paid, and the new term is a subscription with the
 * same `cancel()` — so each point reads true there as well.
 */
export function CommitmentTermsDetail({
  months,
  monthlyPence,
  totalPence,
}: {
  months: number;
  monthlyPence: number;
  totalPence: number;
}) {
  const monthly = formatPence(monthlyPence);

  /* ONE MONTH, NOT THE TOTAL. The commitment is `months` months but the only
     charge at approval is month 1 — a patient who reads the total above and
     nothing else will expect that whole figure to leave their account. */
  const approval: IconPoint = {
    key: 'approval',
    icon: <IconStethoscope />,
    title:
      months === 1 ? (
        <>
          <strong>When a clinician approves, you pay {monthly}.</strong> Not approved? You pay nothing.
        </>
      ) : (
        <>
          <strong>When a clinician approves, you pay your first month ({monthly}).</strong> Not approved? You pay
          nothing.
        </>
      ),
  };

  const points: IconPoint[] =
    months === 1
      ? [
          /* ⚠️ IT IS NOT A ROLLING PLAN, AND THIS POINT USED TO SAY IT WAS
             (corrected 2026-09-04, when the 1-month term became selectable
             again). A one-month plan ENDS after its month and deliberately does
             not auto-renew — that is the whole reason
             `patient-portal/ResubscribeCard` exists — so "a rolling monthly
             plan… before your next monthly charge" promised both a continuation
             that never happens and a charge there is nothing to stop. Same
             failure as the removed exit fee: plausible wording, no code behind
             it, on the one screen where a patient commits money. */
          {
            key: 'plan',
            icon: <IconCalendar />,
            title: (
              <>
                <strong>A one-month plan at {monthly}.</strong> It ends after that month and doesn&rsquo;t renew &mdash;
                to carry on, you start a new plan.
              </>
            ),
          },
          approval,
          /* The clinical carve-out, with the charges (see the header). On a
             one-month plan nothing is ever charged after month 1, so this is the
             carve-out and nothing more — no "stop future months", there are none. */
          {
            key: 'stopping',
            icon: <IconRefresh />,
            title: (
              <>
                <strong>{CARVE_OUT_LEAD}</strong>
                {CARVE_OUT_REST}
              </>
            ),
          },
        ]
      : [
          /* The commitment restated in THIS patient's numbers. A generic
             "minimum term applies" is the sentence people skip; their own
             figures are not. "Billed monthly" is what keeps the total from
             reading as one charge. */
          {
            key: 'commitment',
            icon: <IconPriceTag />,
            title: (
              <>
                <strong>
                  {months} months at {monthly} a month
                </strong>{' '}
                &mdash; {formatPence(totalPence)} in total, billed monthly.
              </>
            ),
          },
          /* WHAT STOPPING ACTUALLY DOES, in the system's own terms (see the ⚠️
             above). The patient's "Stop future months" sets cancel-at-term-end
             and nothing else: `runDue` still bills every month left in the term.
             So the point says the term runs to its end and that stopping does
             not shorten it — the dashboard's own words — and never "only pay
             for the months you've had", which was the promise in the patient's
             favour that orders-service does not keep.

             THE CLINICAL CARVE-OUT IS IN THE SAME POINT, with the charges, not
             buried in the linked terms — a patient worried about side effects
             must see it at the moment they are deciding. */
          {
            key: 'term',
            icon: <IconCalendar />,
            title: (
              <>
                <strong>Your plan runs for all {months} months</strong> &mdash; stopping from your account
                doesn&rsquo;t end it early. {CARVE_OUT_LEAD}
                {CARVE_OUT_REST}
              </>
            ),
          },
          approval,
        ];

  return (
    <>
      <SummaryEyebrow>What you&rsquo;re agreeing to</SummaryEyebrow>
      <IconPoints compact points={points} titleStyle={AGREEMENT_INK} />
    </>
  );
}

/**
 * THE TICK — the affirmative act, and the CTA's condition.
 *
 * Exported separately so a layout can keep it beside the button it gates (see
 * the ⚠️ in the header). `aria-describedby` still points at the tick's own
 * sentence: it is the restatement of the commitment in this patient's numbers,
 * and it must travel with the control rather than be left behind in whatever
 * region the bullets ended up in.
 *
 * SHORTER SINCE 2026-09-26 (the CTO: "reduce the text"). It said "I understand
 * this is a 3-month plan billed monthly, which I can stop from my account and I
 * agree to…", restating the stop promise the points above it had just made. It
 * now names what is being agreed to and nothing else: the plan, billed monthly
 * (or, for one month, that it does not renew), and the terms.
 *
 * ⚠️ THE LABEL IS THE TARGET, SO IT IS AT LEAST 44px TALL (WCAG 2.5.5 — the brief's
 * "keep 44px targets"). The 18px box is too small to aim at; the label around it
 * takes the press. The long tick wrapped to three lines and was 63px on its own;
 * the shorter one is TWO lines of 14px × 1.5 = 42px, measured at 343×42 on a
 * 375px phone and 340×42 at 1440 (review, 2026-09-26) — 2px short. `minHeight`
 * restores the target without padding a rail foot whose every pixel is budgeted
 * (PlanSelection's rail note), and `flex-start` keeps the box on the first line.
 */
/** The tick's press target — the label — is never shorter than this (see above). */
export const TICK_TARGET_MIN_PX = '44px';

export function CommitmentTermsTick({
  months,
  accepted,
  onChange,
}: {
  months: number;
  accepted: boolean;
  onChange: (next: boolean) => void;
}) {
  const isSingleMonth = months === 1;

  return (
    <label
      style={{
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        minHeight: TICK_TARGET_MIN_PX,
        cursor: 'pointer',
        fontSize: '14px',
        lineHeight: 1.5,
      }}
    >
      <input
        type="checkbox"
        checked={accepted}
        onChange={(e) => onChange(e.target.checked)}
        aria-describedby="commitment-terms-detail"
        style={{ width: '18px', height: '18px', flex: 'none', marginTop: '2px', accentColor: 'var(--gps-primary)', cursor: 'pointer' }}
      />
      <span id="commitment-terms-detail">
        {isSingleMonth
          ? 'I agree to this one-month plan, which doesn’t renew, and the '
          : `I agree to this ${months}-month plan, billed monthly, and the `}
        {/* `noopener` beside `noreferrer` to match the footer's legal links.
            `noreferrer` already implies it in every browser we support, but
            the pair is what a reader recognises as "this is deliberate", and
            the day somebody drops the referrer half for analytics reasons the
            opener protection should not leave with it. */}
        <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gps-link)' }}>
          terms and conditions
        </a>
        .
      </span>
    </label>
  );
}

/**
 * The whole gate in ordinary page flow — bullets then tick, one panel.
 *
 * This is what /resubscribe and the in-portal re-subscribe card render, and what
 * the plan step's recommendation route (a single narrow column, no rail) renders
 * too. Only the two-column plan layout needs the halves apart.
 */
export function CommitmentTerms({
  months,
  monthlyPence,
  totalPence,
  accepted,
  onChange,
}: {
  months: number;
  monthlyPence: number;
  totalPence: number;
  accepted: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="gps-jux-panel" style={{ ...commitmentPanelStyle, marginTop: 'var(--gps-s-4)' }}>
      <CommitmentTermsDetail months={months} monthlyPence={monthlyPence} totalPence={totalPence} />
      <div style={{ marginTop: '14px' }}>
        <CommitmentTermsTick months={months} accepted={accepted} onChange={onChange} />
      </div>
    </div>
  );
}
